import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Close as CloseIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useQueryClient } from '@tanstack/react-query';
import Iconify from 'src/components/Iconify';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import {
  applyMergeTransactionBundle,
  buildFuegoWebMessage,
  generatePayloadForContext,
  saveAndValidateMergeBundle,
} from './services/fuegoBundleService';
import { FuegoFlowContext, MainMessage } from './types';

interface Props {
  context: FuegoFlowContext;
  onClose: () => void;
}

export function buildFuegoAppUrl(
  baseUrl: string,
  context: FuegoFlowContext,
  configId: string = 'careflow'
): string {
  const url = new URL(
    baseUrl,
    typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
  );
  const basePath = url.pathname.replace(/\/+$/, '');
  const subPath = context.action === 'merge' ? '/merge' : '';
  url.pathname = `${basePath}${subPath}` || '/';

  const resolvedId = context.id || configId;
  if (resolvedId && !url.searchParams.has('id')) {
    url.searchParams.set('id', resolvedId);
  }

  url.searchParams.set('action', context.action);
  url.searchParams.set('target', context.target);

  if (context.primaryPatientId) {
    url.searchParams.set('patientId', context.primaryPatientId);
  }
  if (context.targetPatientId) {
    url.searchParams.set('targetPatientId', context.targetPatientId);
  }
  if (context.documentReference?.id) {
    url.searchParams.set('documentId', context.documentReference.id);
  }

  return url.toString();
}

export default function FuegoAppEmbeddedDialog({ context, onClose }: Props) {
  const { configurations } = useTenantConfigData();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const [isIframeLoading, setIsIframeLoading] = useState(true);
  const [isGeneratingPayload, setIsGeneratingPayload] = useState(false);
  const [payloadError, setPayloadError] = useState<string | null>(null);
  const [generatedPayload, setGeneratedPayload] = useState<MainMessage | null>(
    context.preloadedPayload || null
  );

  const isIframeReadyRef = useRef(false);
  const hasSentPayloadRef = useRef(false);
  const payloadRef = useRef<MainMessage | null>(context.preloadedPayload || null);

  const rawBaseUrl =
    configurations?.fuegoAppUrl || process.env.REACT_APP_FUEGO_APP_URL || 'http://localhost:3003';

  const configId =
    context.id ||
    configurations?.fuegoAppId ||
    process.env.REACT_APP_FUEGO_APP_ID ||
    'careflow';

  const fuegoUrl = buildFuegoAppUrl(rawBaseUrl, context, configId);

  // Helper to transmit the generated webMessagePayload to the iframe
  const sendPayloadToIframe = useCallback((payload: MainMessage, force = false) => {
    if (!iframeRef.current?.contentWindow) return;
    if (hasSentPayloadRef.current && !force) return;

    hasSentPayloadRef.current = true;
    const messageEnvelope = buildFuegoWebMessage(payload);
    console.debug('[FuegoAppEmbeddedDialog] Sending webMessagePayload to Fuego App:', {
      topic: messageEnvelope.topic,
      transactionId: payload.transactionId,
      authorizedPatientId: payload.authorizedPatientId,
      sourcesCount: Object.keys(payload.sources || {}).length,
      sourcesList: Object.entries(payload.sources || {}).map(([key, src]) => ({
        key,
        sourceName: src.sourceName,
        entriesCount: src.bundle?.entry?.length || 0,
      })),
      mainPerson: payload.mainPerson?.name,
      options: payload.options,
      messageEnvelope,
    });
    iframeRef.current.contentWindow.postMessage(messageEnvelope, '*');
    setIsIframeLoading(false);
  }, []);

  // Generate FHIR bundles asynchronously based on the flow context
  const assemblePayload = useCallback(async () => {
    if (context.preloadedPayload) {
      setGeneratedPayload(context.preloadedPayload);
      payloadRef.current = context.preloadedPayload;
      if (isIframeReadyRef.current) {
        sendPayloadToIframe(context.preloadedPayload, true);
      }
      return;
    }

    try {
      setIsGeneratingPayload(true);
      setPayloadError(null);

      const payload = await generatePayloadForContext(context);

      if (payload) {
        setGeneratedPayload(payload);
        payloadRef.current = payload;
        // If iframe is already waiting in ready state, dispatch immediately
        if (isIframeReadyRef.current) {
          sendPayloadToIframe(payload, true);
        }
      }
    } catch (err: any) {
      console.error('[FuegoAppEmbeddedDialog] Error generating FHIR bundles for Fuego App:', err);
      setPayloadError(err?.message || 'Failed to assemble FHIR bundles for Fuego App');
    } finally {
      setIsGeneratingPayload(false);
    }
  }, [context, sendPayloadToIframe]);

  useEffect(() => {
    assemblePayload();
  }, [assemblePayload]);

  // Listen for iframe events (ready handshake, merge cancel, merge validate, merge complete)
  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (!event.data) return;

      const isFuegoTopic =
        event.data.topic === 'iframe-tester' ||
        event.data.topic === 'iframe-fuego-app' ||
        event.data.topic === 'fuego-app-iframe-tester';

      if (!isFuegoTopic) return;

      // Handshake: iframe announced it is ready to receive data
      if (event.data.message === 'ready' || event.data.action === 'ready') {
        console.debug('[FuegoAppEmbeddedDialog] Received ready handshake from Fuego App iframe');
        isIframeReadyRef.current = true;
        const payloadToSend = payloadRef.current || generatedPayload;
        if (payloadToSend) {
          sendPayloadToIframe(payloadToSend, true);
        }
      }

      // Close modal on merge cancel, complete, or close
      if (
        event.data.action === 'merge-cancel' ||
        event.data.action === 'merge-complete' ||
        event.data.action === 'merge-close'
      ) {
        onClose();
      }

      // Handle Step Validation from Fuego App
      if (event.data.action === 'merge-validate') {
        const bundle = event.data.data;
        const screenId = event.data.screenId;
        console.debug('[FuegoAppEmbeddedDialog] Received step validation event for bundle:', {
          bundleId: bundle?.id,
          step: event.data.step,
          screenId,
        });
        saveAndValidateMergeBundle(bundle)
          .then(() => {
            console.debug('[FuegoAppEmbeddedDialog] Step validation succeeded for bundle:', bundle?.id);
            iframeRef.current?.contentWindow?.postMessage(
              {
                topic: 'iframe-fuego-app',
                action: 'merge-validated',
                bundleId: bundle?.id,
                step: event.data.step,
                screenId,
              },
              '*'
            );
          })
          .catch((err: any) => {
            console.error('[FuegoAppEmbeddedDialog] Step validation error:', err);
            enqueueSnackbar(err?.message || 'Error validating merge selections with FHIR server', {
              variant: 'error',
              autoHideDuration: 6000,
            });
            iframeRef.current?.contentWindow?.postMessage(
              {
                topic: 'iframe-fuego-app',
                action: 'merge-error',
                status: 'error',
                error: err?.message,
                message: err?.message,
                bundleId: bundle?.id,
                step: event.data.step,
                screenId,
              },
              '*'
            );
          });
      }

      // Handle Merge Finalization from Fuego App
      if (event.data.action === 'merge-bundle' || event.data.action === 'merge-finalize') {
        const bundle = event.data.data;
        console.debug('[FuegoAppEmbeddedDialog] Received finalize merge event for bundle:', bundle?.id);

        // Defensive check: at least 1 entry is required
        if (!bundle || !Array.isArray(bundle.entry) || bundle.entry.length === 0) {
          console.warn('[FuegoAppEmbeddedDialog] Rejected finalize merge: bundle contains 0 entries');
          enqueueSnackbar('Cannot apply merge: output bundle contains no resource entries.', {
            variant: 'warning',
          });
          iframeRef.current?.contentWindow?.postMessage(
            {
              topic: 'iframe-fuego-app',
              action: 'merge-error',
              status: 'error',
              message: 'Bundle has no entries to merge',
              bundleId: bundle?.id,
            },
            '*'
          );
          return;
        }

        (async () => {
          try {
            // Step 1: Pre-flight validation using PUT /Bundle/{id}
            await saveAndValidateMergeBundle(bundle);

            // Step 2: Apply transaction using POST /
            await applyMergeTransactionBundle(bundle);

            // Step 3: Success notification
            enqueueSnackbar('Patient merge applied and saved successfully!', {
              variant: 'success',
            });

            // Step 4: Confirm to child iframe
            iframeRef.current?.contentWindow?.postMessage(
              {
                topic: 'iframe-fuego-app',
                action: 'merge-confirmed',
                status: 'confirmed',
                message: 'Merge transaction applied successfully',
                bundleId: bundle.id,
              },
              '*'
            );

            // Invalidate query caches to refresh patient views in Careflow
            queryClient.invalidateQueries({ queryKey: ['Patient'] });
            queryClient.invalidateQueries({ queryKey: ['Observation'] });
            queryClient.invalidateQueries({ queryKey: ['Condition'] });
            queryClient.invalidateQueries({ queryKey: ['DocumentReference'] });
          } catch (err: any) {
            console.error('[FuegoAppEmbeddedDialog] Finalize merge execution failed:', err);
            enqueueSnackbar(err?.message || 'Failed to finalize and apply patient merge', {
              variant: 'error',
              autoHideDuration: 8000,
            });

            // Notify iframe of failure
            iframeRef.current?.contentWindow?.postMessage(
              {
                topic: 'iframe-fuego-app',
                action: 'merge-error',
                status: 'error',
                message: err?.message,
                error: err?.message,
                bundleId: bundle?.id,
              },
              '*'
            );
          }
        })();
      }
    }

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [enqueueSnackbar, generatedPayload, onClose, queryClient, sendPayloadToIframe]);

  const handleIframeLoad = () => {
    // Fallback: If the child iframe does not implement or send a ready handshake within 3.5s,
    // dispatch the payload if not already sent
    window.setTimeout(() => {
      const payloadToSend = payloadRef.current || generatedPayload;
      if (payloadToSend && !hasSentPayloadRef.current) {
        console.debug(
          '[FuegoAppEmbeddedDialog] Fallback timeout: iframe did not send ready handshake, sending payload now'
        );
        sendPayloadToIframe(payloadToSend);
      }
    }, 3500);
  };

  const actionTitle = context.action === 'merge' ? 'Merge on Fuego App' : 'View on Fuego App';
  const targetSubtitle =
    context.target === 'patient'
      ? `Patient Record: ${context.targetPatientId || context.primaryPatientId || 'N/A'}`
      : `Attachment: ${
          context.documentReference?.content?.[0]?.attachment?.title ||
          context.documentReference?.description ||
          context.documentReference?.id ||
          'Document'
        }`;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '85vh', minHeight: 650 }}>
      {/* Dialog Header */}
      <DialogTitle
        sx={{
          py: 1.5,
          px: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.neutral',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Iconify
            icon={context.action === 'merge' ? 'mdi:call-merge' : 'eva:external-link-fill'}
            sx={{ width: 24, height: 24, color: 'primary.main' }}
          />
          <Box>
            <Typography variant="subtitle1" component="div" sx={{ fontWeight: 600 }}>
              {actionTitle}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {targetSubtitle}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Close">
            <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </DialogTitle>

      {/* Dialog Content with Iframe & Loading Overlay */}
      <DialogContent sx={{ p: 0, position: 'relative', flexGrow: 1, overflow: 'hidden' }}>
        {(isIframeLoading || isGeneratingPayload) && !payloadError && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'background.paper',
              zIndex: 2,
            }}
          >
            <CircularProgress size={42} thickness={4} />
            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary', fontWeight: 500 }}>
              {isGeneratingPayload
                ? 'Retrieving and assembling patient FHIR bundles...'
                : 'Loading Fuego App...'}
            </Typography>
          </Box>
        )}

        {payloadError && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 3,
              backgroundColor: 'background.paper',
              zIndex: 3,
            }}
          >
            <Alert
              severity="error"
              action={
                <Button
                  color="inherit"
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={() => {
                    hasSentPayloadRef.current = false;
                    assemblePayload();
                  }}
                >
                  Retry
                </Button>
              }
              sx={{ maxWidth: 500 }}
            >
              {payloadError}
            </Alert>
          </Box>
        )}

        <iframe
          ref={iframeRef}
          id="fuego-app-iframe"
          name="fuego_app_iframe"
          title="Fuego App"
          src={fuegoUrl}
          onLoad={handleIframeLoad}
          sandbox="allow-same-origin allow-scripts allow-modals allow-popups allow-forms"
          style={{
            border: 'none',
            width: '100%',
            height: '100%',
            display: 'block',
          }}
        />
      </DialogContent>

      {/* Dialog Footer Actions */}
      <DialogActions
        sx={{
          py: 1,
          px: 2.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          justifyContent: 'space-between',
        }}
      >
        <Button variant="outlined" size="small" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Box>
  );
}
